export default class BaseBookingComponent extends HTMLElement {
    static BASE_URL = 'https://bookings.travelclick.com';

    whenLoaded = Promise.all([
        customElements.whenDefined('c-calendar'),
        customElements.whenDefined('calendar-range'),
        customElements.whenDefined('calendar-date')
    ]);

    constructor(m) {
        super(m);

        // Common properties
        this.isDateInvalid = false;

        // Common elements
        this.$form = this.querySelector('[data-form]');
        this.$formInner = this.querySelector('[data-form-inner]');
        this.$dateLabelCheckIn = this.querySelector('[data-check-in-label]');
        this.$dateLabelCheckOut = this.querySelector('[data-check-out-label]');
        this.$dateError = this.querySelector('[data-dates-error]');

        // Labels
        this.dateLabelCheckInString = this.$dateLabelCheckIn.getAttribute('data-label');
        this.dateLabelCheckOutString = this.$dateLabelCheckOut.getAttribute('data-label');

        // Bind methods
        this._onCalendarChange = this._onCalendarChange.bind(this);
        this.onFormSubmit = this.onFormSubmit.bind(this);
    }

    connectedCallback() {
        this.whenLoaded.then(() => {
            requestAnimationFrame(() => {
                this.$calendarRoot = this.querySelector('[data-calendar]');
                this.$calendar = this.$calendarRoot.$calendar;

                this._bindEvents();
                this.bindEvents?.();
                this.updateDateLabel(this.$calendar.value);
            });
        });
    }

    disconnectedCallback() {
        this._unbindEvents();
        this.unbindEvents?.();
    }

    // =============================================================================
    // Events
    // =============================================================================
    _bindEvents() {
        this.$form.addEventListener('submit', this.onFormSubmit);
        this.$calendar.addEventListener('change', this._onCalendarChange);
    }

    _unbindEvents() {
        this.$form.removeEventListener('submit', this.onFormSubmit);
        this.$calendar.removeEventListener('change', this._onCalendarChange);
    }

    _onCalendarChange(e) {
        this.updateDateLabel(e.target.value);
        if (this.isDateInvalid) {
            this.resetCalendarError();
        }

        this.onCalendarChange?.(e);
    }

    onFormSubmit(e) {
        e.preventDefault();

        const dates = this.checkAndFormatDates();
        if (!dates) return;

        const { checkIn, checkOut } = dates;

        const data = new FormData(this.$form);
        data.append(this.getCheckInDateField(), checkIn);
        data.append(this.getCheckOutDateField(), checkOut);

        this.buildUrl(data);
    }

    // =============================================================================
    // Methods
    // =============================================================================
    updateDateLabel(value) {
        const [from, to] = value.split('/');
        if (from) {
            this.$dateLabelCheckIn.innerHTML = this.dateLabelCheckInString.replace('{date}', from);
        }
        if (to) {
            this.$dateLabelCheckOut.innerHTML = this.dateLabelCheckOutString.replace('{date}', to);
        }
    }

    checkAndFormatDates() {
        this.$dateError.style.display = 'none';

        const dates = this.$calendar.value.split('/');
        const checkIn = dates[0]?.trim();
        const checkOut = dates[1]?.trim();

        if (!checkIn || !checkOut || new Date(checkIn) >= new Date(checkOut)) {
            this.showCalendarError();
            return;
        }

        this.resetCalendarError();
        return { checkIn, checkOut };
    }

    showCalendarError() {
        this.$dateError.style.display = 'block';
        this.$dateError.setAttribute('aria-live', 'polite');
        this.$calendarRoot.setAttribute('aria-labelledby', this.$dateError.id);
        this.$calendarRoot.focus();
        this.isDateInvalid = true;

        this.onCalendarError?.();
    }

    resetCalendarError() {
        this.isDateInvalid = false;
        this.$calendarRoot.setAttribute('aria-labelledby', 'dates');
        this.$dateError.setAttribute('aria-live', null);
        this.$dateError.style.display = 'none';
    }

    buildUrl(data) {
        const url = new URL(`${this.constructor.BASE_URL}/${data.get(this.getPropertyField())}`);

        const formatDateToLocale = (date) => {
            const parsedDate = new Date(date);
            const year = parsedDate.getUTCFullYear();
            const month = String(parsedDate.getUTCMonth() + 1).padStart(2, '0');
            const day = String(parsedDate.getUTCDate()).padStart(2, '0');
            return `${month}/${day}/${year}`;
        };

        const checkInDate = data.get(this.getCheckInDateField());
        const checkOutDate = data.get(this.getCheckOutDateField());

        if (checkInDate) {
            url.searchParams.append('datein', formatDateToLocale(checkInDate));
        }
        if (checkOutDate) {
            url.searchParams.append('dateout', formatDateToLocale(checkOutDate));
        }

        this.appendRoomDataToUrl(url, data);

        window.open(url.toString(), '_blank');
        this.resetForm();
    }

    appendRoomDataToUrl(url, data) {
        // To be overridden by subclasses for room-specific data handling
    }

    resetRooms() {
        // To be overridden by subclasses for room-specific reset
    }

    resetForm() {
        this.$form.reset();
        this.$calendarRoot.reset();
        this.updateDateLabel('');
        this.resetCalendarError();
        this.resetRooms();
        this.onReset?.();
    }

    // Abstract methods (to be implemented by subclasses)
    getCheckInDateField() {
        throw new Error('getCheckInDateField() must be implemented in subclasses');
    }

    getCheckOutDateField() {
        throw new Error('getCheckOutDateField() must be implemented in subclasses');
    }

    getPropertyField() {
        throw new Error('getPropertyField() must be implemented in subclasses');
    }

    isValidISO8601Range(inputText) {
        // Define the regex pattern for the ISO-8601 date range format
        const pattern = /^\d{4}-\d{2}-\d{2}\/\d{4}-\d{2}-\d{2}$/;

        // Check if the input text matches the pattern
        if (!pattern.test(inputText)) {
            return false;
        }

        // Split the input text into start and end dates
        const [startDate, endDate] = inputText.split('/');

        // Helper function to check if a date is valid
        function isValidDate(dateString) {
            const date = new Date(dateString);
            return !isNaN(date.getTime()) && date.toISOString().startsWith(dateString);
        }

        // Validate both start and end dates
        if (!isValidDate(startDate) || !isValidDate(endDate)) {
            return false;
        }

        // Ensure the start date is before or equal to the end date
        if (new Date(startDate) > new Date(endDate)) {
            return false;
        }

        return true;
    }

    formatDateRange(date) {
        if (!this.isValidISO8601Range(date)) {
            return '-';
        }
        const [startStr, endStr] = date.split('/');
        return `${this.formatDate(startStr)} / ${this.formatDate(endStr)}`;
    }

    formatDate(str) {
        const [year, month, day] = str.split('-').map(Number);
        const lang = document.documentElement.lang || 'en-US';
        const date = new Date(year, month - 1, day);
        return date.toLocaleDateString(lang, {
            day: '2-digit',
            month: 'short',
            year: '2-digit'
        });
    }
}
